/**
 * Subscription store - manages subscription and billing state
 */

import { create } from 'zustand';
import { subscriptionsApi } from '@/services/api';
import { validateRedirectUrl } from '@/utils/security';
import type {
  SubscriptionPlanResponse,
  SubscriptionResponse,
  UsageSummary,
  PaymentResponse,
} from '@neokanban/types';

interface SubscriptionState {
  // State
  currentSubscription: SubscriptionResponse | null;
  availablePlans: SubscriptionPlanResponse[];
  usage: UsageSummary | null;
  invoices: PaymentResponse[];
  canUpgrade: boolean;
  isLoading: boolean;
  error: string | null;
  subscriptionFetchedAt: number | null; // Timestamp of last current-subscription fetch
  plansFetchedAt: number | null; // Timestamp of last plans fetch

  // Actions
  fetchCurrentSubscription: (force?: boolean) => Promise<void>;
  fetchPlans: (force?: boolean) => Promise<void>;
  fetchInvoices: (limit?: number) => Promise<void>;
  createCheckout: (
    planId: string,
    billingPeriod: 'monthly' | 'yearly',
    successUrl?: string,
    cancelUrl?: string
  ) => Promise<string>;
  upgradeSubscription: (planId: string, billingPeriod: 'monthly' | 'yearly') => Promise<void>;
  cancelSubscription: (immediately?: boolean) => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  currentSubscription: null,
  availablePlans: [],
  usage: null,
  invoices: [],
  canUpgrade: false,
  isLoading: false,
  error: null,
  subscriptionFetchedAt: null,
  plansFetchedAt: null,
};

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  ...initialState,

  // Fetch current subscription with usage
  fetchCurrentSubscription: async (force = false) => {
    const { subscriptionFetchedAt } = get();
    const now = Date.now();

    // Skip fetch if data was fetched less than 30 seconds ago (unless forced)
    if (!force && subscriptionFetchedAt && now - subscriptionFetchedAt < 30000) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const data = await subscriptionsApi.getCurrentSubscription();
      set({
        currentSubscription: data.subscription,
        usage: data.usage,
        canUpgrade: data.canUpgrade,
        availablePlans: data.availablePlans,
        subscriptionFetchedAt: now,
        // This endpoint also returns plans, so refresh their TTL too
        plansFetchedAt: now,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch subscription',
        isLoading: false,
      });
      throw error;
    }
  },

  // Fetch available plans
  fetchPlans: async (force = false) => {
    const { plansFetchedAt } = get();
    const now = Date.now();

    // Skip fetch if data was fetched less than 30 seconds ago (unless forced)
    if (!force && plansFetchedAt && now - plansFetchedAt < 30000) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const plans = await subscriptionsApi.listPlans();
      set({
        availablePlans: plans,
        plansFetchedAt: now,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch plans',
        isLoading: false,
      });
      throw error;
    }
  },

  // Fetch invoice history
  fetchInvoices: async (limit = 10) => {
    set({ isLoading: true, error: null });
    try {
      const data = await subscriptionsApi.listInvoices(limit);
      set({
        invoices: data.invoices,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch invoices',
        isLoading: false,
      });
      throw error;
    }
  },

  // Create checkout session and return URL
  createCheckout: async (planId, billingPeriod, successUrl, cancelUrl) => {
    set({ isLoading: true, error: null });
    try {
      // Validate redirect URLs for security (prevent open redirect attacks)
      const validatedSuccessUrl = successUrl ? validateRedirectUrl(successUrl) : undefined;
      const validatedCancelUrl = cancelUrl ? validateRedirectUrl(cancelUrl) : undefined;

      const data = await subscriptionsApi.createCheckoutSession({
        planId,
        billingPeriod,
        ...(validatedSuccessUrl && { successUrl: validatedSuccessUrl }),
        ...(validatedCancelUrl && { cancelUrl: validatedCancelUrl }),
      });

      // Validate the checkout URL returned from backend before returning it
      const validatedCheckoutUrl = validateRedirectUrl(
        data.checkoutUrl,
        [window.location.origin, 'https://checkout.stripe.com']
      );

      set({ isLoading: false });
      return validatedCheckoutUrl;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to create checkout session',
        isLoading: false,
      });
      throw error;
    }
  },

  // Upgrade subscription
  upgradeSubscription: async (planId, billingPeriod) => {
    set({ isLoading: true, error: null });
    try {
      const subscription = await subscriptionsApi.upgradeSubscription({
        planId,
        billingPeriod,
      });
      set({
        currentSubscription: subscription,
        isLoading: false,
      });

      // Refresh full subscription data (includes usage)
      await get().fetchCurrentSubscription();

      // Also refresh organization data to sync tier changes
      // This ensures the UI immediately reflects the new tier and limits
      const { useAuthStore } = await import('./authStore');
      const authStore = useAuthStore.getState();
      const currentOrgId = authStore.currentOrgId;

      if (currentOrgId) {
        try {
          const { organizationsApi } = await import('@/services/api');
          const org = await organizationsApi.get(currentOrgId);
          authStore.setCurrentOrganization(org);
        } catch (orgError) {
          // Log but don't throw - subscription upgrade succeeded
          console.error('Failed to refresh organization after upgrade:', orgError);
        }
      }
    } catch (error: any) {
      set({
        error: error.message || 'Failed to upgrade subscription',
        isLoading: false,
      });
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async (immediately = false) => {
    set({ isLoading: true, error: null });
    try {
      const subscription = await subscriptionsApi.cancelSubscription(immediately);
      set({
        currentSubscription: subscription,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to cancel subscription',
        isLoading: false,
      });
      throw error;
    }
  },

  // Reactivate subscription
  reactivateSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      const subscription = await subscriptionsApi.reactivateSubscription();
      set({
        currentSubscription: subscription,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to reactivate subscription',
        isLoading: false,
      });
      throw error;
    }
  },

  // Open Stripe customer portal
  openCustomerPortal: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await subscriptionsApi.getCustomerPortal();
      set({ isLoading: false });
      // Open portal in new window
      window.open(data.portalUrl, '_blank');
    } catch (error: any) {
      set({
        error: error.message || 'Failed to open customer portal',
        isLoading: false,
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Reset store
  reset: () => set(initialState),
}));
